import { useState, useEffect } from "react";
import { Users, Check, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { getSocialsListAPI, approveSocialAPI } from "../../api/miningApi";

export default function MiningSocial() {
  const [socials, setSocials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [approvingIds, setApprovingIds] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState("all");
  const pageSize = 10;

  useEffect(() => {
    fetchSocialsData();
  }, [currentPage, statusFilter]);

  const fetchSocialsData = async () => {
    try {
      setLoading(true);
      let statusParam = null;
      if (statusFilter === "false") statusParam = false;
      else if (statusFilter === "true") statusParam = true;

      const response = await getSocialsListAPI(statusParam);
      if (response.status) {
        setSocials(response.data.data || []);
        setTotalRecords(response.data.totalRows || 0);
        setTotalPages(response.data.totalPages || Math.ceil((response.data.totalRows || 0) / pageSize));
      }
    } catch (error) {
      console.error("Failed to fetch socials data:", error);
      setSocials([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveSocial = async (socialId) => {
    try {
      setApprovingIds((prev) => new Set([...prev, socialId]));
      const response = await approveSocialAPI(socialId);
      if (response.success) {
        await fetchSocialsData();
      } else {
        console.error("Failed to approve social:", response.message);
      }
    } catch (error) {
      console.error("Error approving social:", error);
    } finally {
      setApprovingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(socialId);
        return newSet;
      });
    }
  };

  const renderPagination = () => {
    return Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
      if (
        page === 1 ||
        page === totalPages ||
        (page >= currentPage - 1 && page <= currentPage + 1)
      ) {
        return (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            className={`h-8 w-8 rounded text-xs font-medium transition-colors ${
              page === currentPage
                ? "bg-ginox-green/20 border border-ginox-green/60 text-ginox-green"
                : "border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
            }`}
          >
            {page}
          </button>
        );
      }
      if (page === currentPage - 2 || page === currentPage + 2) {
        return (
          <span key={page} className="h-8 w-8 flex items-center justify-center text-muted-foreground text-xs">
            ...
          </span>
        );
      }
      return null;
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          Mining
        </div>
        <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">Social <span className="text-gradient-ginox">Management</span></h1>
        <p className="text-sm text-muted-foreground">Manage social media connections and activities</p>
      </div>

      {/* Filters */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Status Filter
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-48 rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              <option value="all">All Data</option>
              <option value="true">Status Approved</option>
              <option value="false">Status Pending</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              {["User ID", "Platform", "Identifier", "Status", "Tokens Earned", "Actions"].map((h) => (
                <th key={h} className="border-b border-border/70 px-4 py-3 text-left text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="py-12 text-center text-sm text-muted-foreground">Loading...</td>
              </tr>
            ) : socials.length > 0 ? (
              socials.map((social, index) => (
                <tr key={social._id || index} className="group hover:bg-white/[0.02] transition-colors">
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                    {social.userId || "N/A"}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                    {social.platformUrl || "N/A"}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                    {social.identifier || "N/A"}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                      social.isJoined
                        ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                        : "bg-red-500/10 text-red-400 border border-red-500/20"
                    }`}>
                      {social.isJoined ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm">
                    {social.tokensEarned ? (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Yes</span>
                    ) : (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20">No</span>
                    )}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm">
                    {!social.isJoined ? (
                      <button
                        onClick={() => handleApproveSocial(social._id)}
                        disabled={approvingIds.has(social._id)}
                        className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                      >
                        {approvingIds.has(social._id) ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Approving...
                          </>
                        ) : (
                          <>
                            <Check className="w-3.5 h-3.5" />
                            Approve
                          </>
                        )}
                      </button>
                    ) : (
                      <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                        Approved
                      </span>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="py-12 text-center text-sm text-muted-foreground">
                  <div className="flex flex-col items-center gap-2">
                    <Users className="w-8 h-8 text-muted-foreground/30" />
                    <p>No social connections found</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col items-center gap-3 md:flex-row md:items-center md:justify-between">
          <p className="text-xs text-muted-foreground">
            {loading
              ? "Loading..."
              : `Showing ${Math.min((currentPage - 1) * pageSize + 1, totalRecords)} to ${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords} social connections`}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1 || loading}
              className="h-8 w-8 rounded border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            {!loading && renderPagination()}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || loading}
              className="h-8 w-8 rounded border border-border/70 bg-white/[0.02] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
